import React, { useState, useEffect, useCallback } from 'react';
import { trainingAPI } from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';
import './TrainingActivityReport.css';
import './TrainingMonitorReport.css';

function formatDate(d) {
  if (!d) return '—';
  return formatDisplayDate(d);
}

function csvEscape(val) {
  if (val == null) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, headers, rows) {
  const line = (arr) => arr.map(csvEscape).join(',');
  const body = [line(headers), ...rows.map((r) => line(r))].join('\r\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
}

export default function TrainingMonitorReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [maxHours, setMaxHours] = useState('20');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runReport = useCallback(async (hoursValue) => {
    const num = parseFloat(hoursValue, 10);
    if (Number.isNaN(num) || num < 0) {
      setError('Enter a valid max hours (number ≥ 0).');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await trainingAPI.reportMonitor({ max_hours: num });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run when max hours changes (debounced) or when refreshKey changes (e.g. re-open report)
  useEffect(() => {
    const timer = setTimeout(() => runReport(maxHours), 500);
    return () => clearTimeout(timer);
  }, [maxHours, refreshKey, runReport]);

  const handleReset = useCallback(() => {
    setMaxHours('20');
  }, []);

  const handleExportCsv = useCallback(() => {
    const headers = ['Staff', 'Window begin', 'Window end', 'Total hours', 'Comment'];
    const rowData = rows.map((row) => {
      const staffName = [row.last_name, row.first_name].filter(Boolean).join(', ') || '';
      const beginStr = row.training_begin ? (typeof row.training_begin === 'string' && row.training_begin.length >= 10 ? row.training_begin.slice(0, 10) : formatDisplayDate(row.training_begin)) : '';
      const endStr = row.training_end ? (typeof row.training_end === 'string' && row.training_end.length >= 10 ? row.training_end.slice(0, 10) : formatDisplayDate(row.training_end)) : '';
      return [staffName, beginStr, endStr, row.total_hours ?? 0, row.comment || ''];
    });
    downloadCsv('training-monitor-report.csv', headers, rowData);
  }, [rows]);

  return (
    <div className="training-monitor-report">
      <div className="training-report-toolbar no-print">
        <div className="training-report-toolbar-left">
          <h3 className="training-report-title">Training Monitor Report{tenantCode ? ` — ${tenantCode}` : ''}</h3>
          <p className="training-report-description">Staff whose total training hours in the current window are at or below the threshold.</p>
          <div className="training-report-filters">
            <label htmlFor="training-monitor-max-hours">Max hours (threshold): </label>
            <input
              id="training-monitor-max-hours"
              type="number"
              min="0"
              step="0.5"
              value={maxHours}
              onChange={(e) => setMaxHours(e.target.value)}
            />
          </div>
        </div>
        <div className="training-report-toolbar-actions">
          <button type="button" className="btn btn-secondary" onClick={handleReset}>Reset</button>
          <button type="button" className="btn btn-secondary" onClick={handleExportCsv}>Export CSV</button>
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      <div className="training-report-print-header print-only">
        <h1>Training Monitor Report{tenantCode ? ` — ${tenantCode}` : ''}</h1>
      </div>
      {error && <p className="training-report-error">{error}</p>}
      <div className="training-report-table-wrap">
        {loading && rows.length === 0 ? (
          <p>Loading...</p>
        ) : (
          <table className="training-report-table">
            <thead>
              <tr>
                <th>Staff</th>
                <th>Window begin</th>
                <th>Window end</th>
                <th>Total hours</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5}>No staff at or below this threshold.</td></tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.staff_id}>
                    <td>{[row.last_name, row.first_name].filter(Boolean).join(', ') || '—'}</td>
                    <td>{formatDate(row.training_begin)}</td>
                    <td>{formatDate(row.training_end)}</td>
                    <td>{row.total_hours ?? 0}</td>
                    <td>{row.comment || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
